//@ts-ignore
import UserService from '@/services/UserService'
import { TRANSLATION_SAVE_PAYLOAD } from '@/models/Common/Translation'
import i18n from '@/lang/index'
import { ERR_API_KEY } from '@/constants'
import main from '@/main'
import moment from 'moment'
import momentTimeZone from 'moment-timezone'
import { EUserStatus } from '@/models'

export const decodeHtml = (html: string) => {
  const txt = document.createElement('textarea')
  txt.innerHTML = html
  return txt.value
}

/**
 * When content is rendered with v-html, embedded <img> (e.g. from the rich text editor)
 * is not a Vue child — use this from a delegated @click on the container.
 * Returns null if the click was not on an image inside boundaryRoot.
 */
export const getInlineEditorImageSrcFromClick = (
  e: MouseEvent,
  boundaryRoot: HTMLElement
): string | null => {
  const target = e.target as HTMLElement | null
  if (!target) return null
  const img = target.closest('img')
  if (!img || !boundaryRoot.contains(img)) return null
  const el = img
  const src =
    el.currentSrc ||
    el.getAttribute('src') ||
    el.dataset.mceSrc ||
    ''
  const trimmed = src.trim()
  return trimmed || null
}

export const handleTruncateContent = (content: string) => {
  const wrappedContent = `<div>${content}</div>`
  const doc = new DOMParser().parseFromString(wrappedContent, 'text/html')
  const temp = doc.body.firstElementChild as HTMLElement
  if (!temp) return content

  const editorWrapper = document.createElement('div')
  editorWrapper.className = 'editor-data'
  editorWrapper.innerHTML = temp.innerHTML

  return keepFirstElementOnly(editorWrapper)
}

export const handleFlattenedEditorContent = (content: string) => {
  if (!content) return ''

  const wrapped = `<div>${replaceVideoAndParent(content)}</div>`
  const doc = new DOMParser().parseFromString(wrapped, 'text/html')
  const temp = doc.body.firstElementChild as HTMLElement
  if (!temp) return content

  const hasHTMLTags = temp.querySelector('*') !== null

  const editorWrapper = document.createElement('div')
  editorWrapper.className = hasHTMLTags
    ? 'editor-data'
    : 'editor-data editor-text-only'
  editorWrapper.innerHTML = temp.innerHTML

  const blockTags = [
    'address',
    'article',
    'aside',
    'blockquote',
    'canvas',
    'dd',
    'div',
    'dl',
    'dt',
    'fieldset',
    'figcaption',
    'figure',
    'footer',
    'form',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'header',
    'hr',
    'li',
    'main',
    'nav',
    'noscript',
    'ol',
    'p',
    'pre',
    'section',
    'table',
    'tfoot',
    'ul',
    'video'
  ]

  const blockPattern = new RegExp(
    String.raw`</(?:${blockTags.join('|')})>\s*<(?:(?:${blockTags.join('|')}))[^>]*>`,
    'gi'
  )

  // Làm phẳng nội dung HTML
  let flattened = editorWrapper.innerHTML
    .replace(blockPattern, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\n+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  // Decode các entity (&gt; -> >)
  const textarea = document.createElement('textarea')
  textarea.innerHTML = flattened
  flattened = textarea.value

  // Gán lại vào wrapper
  editorWrapper.innerHTML = flattened

  return editorWrapper.outerHTML
}


export const keepFirstElementOnly = (wrapper: HTMLElement) => {
  const strongTags = wrapper.querySelectorAll('strong')

  strongTags.forEach(strong => {
    const tables = strong.querySelectorAll('table')
    const iframes = strong.querySelectorAll('iframe')

    iframes.forEach(iframe => {
      iframe.parentElement?.classList?.add('m-0')
      iframe.style.width = '40px'
      iframe.style.height = '30px'
    })
    tables.forEach(table => {
      table.style.width = '100%'
      table.style.height = '30px'
    })
  })

  return wrapper.outerHTML
}

export const isChatSystemMessage = (chatContent: string): boolean => {
  const text = chatContent?.split(',')?.[0]
  return ['LEAVE_GROUP_CHAT', 'LEAVE_SYSTEM'].includes(text)
}

export const isNavChatSystemMessage = (code: string): boolean => {
  const rawText = new DOMParser().parseFromString(
    `<div>${code}</div>`,
    'text/html'
  ).body.innerText
  const text = rawText?.split(',')?.[0]
  return [
    'ATTACH_FILE',
    'MESSAGE_DELETED',
    'LEAVE_GROUP_CHAT',
    'LEAVE_SYSTEM'
  ].includes(text)
}

export const sanitizeContentEditableInput = (rawHtml: string): string => {
  const wrapper = document.createElement('div')
  wrapper.innerHTML = rawHtml.trim()

  const children = Array.from(wrapper.childNodes)
  const normalizedLines: string[] = []

  let buffer = ''

  children.forEach(node => {
    if (node.nodeName === 'DIV') {
      if (buffer.trim()) {
        normalizedLines.push(`<div>${normalizeLine(buffer)}</div>`)
        buffer = ''
      }
      normalizedLines.push(
        `<div>${normalizeLine((node as HTMLElement).innerHTML)}</div>`
      )
    } else {
      buffer +=
        node instanceof Element ? node.outerHTML : node.textContent || ''
    }
  })

  if (buffer.trim()) {
    normalizedLines.push(`<div>${normalizeLine(buffer)}</div>`)
  }

  return normalizedLines.join('')
}

function normalizeLine(line: string): string {
  return line
    .replace(/^ +/g, m => '&nbsp;'.repeat(m.length))
    .replace(/ {2,}/g, m => '&nbsp;'.repeat(m.length))
}

export function extractTextAreaContentFromData(
  text: string
): { indent: string; content: string }[] {
  const normalized = text.replace(/\r\n?|\n/g, '\n')
  const lines = normalized.split('\n')

  return lines
    .map(line => {
      const match = new RegExp(/^(\s*)(.*)$/).exec(line)
      return {
        indent: match?.[1] || '',
        content: match?.[2] || ''
      }
    })
    .filter(
      line => line.content.trim() !== '' && containsJapanese(line.content)
    )
}

export function extractContentFromContentEditableOrTiny(
  html: string
): {
  doc: Document
  textNodes: {
    node: Text
    originalText: string
    indent: string
  }[]
} {
  const doc = new DOMParser().parseFromString(html, 'text/html')

  const textNodes: {
    node: Text
    originalText: string
    indent: string
  }[] = []

  function recurse(el: Node) {
    // Nếu node là một element và có class "mention" thì bỏ qua
    if (
      el.nodeType === Node.ELEMENT_NODE &&
      (el as Element).classList.contains('mention')
    ) {
      return
    }

    for (const child of el.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        const raw = child.textContent || ''

        const replaced = raw
          .replace('\u00A0', ' ') // nbsp
          .replace('\u3000', ' ') // full-width space
          .replace('\t', ' ') // tab
          .replace(/\r?\n/g, ' ') // newline
          .replace(/^ +/, match => ' '.repeat(match.length)) // keep current space

        const match = new RegExp(/^([ ]*)(.*)$/).exec(replaced)
        const indent = match?.[1] || ''
        const content = match?.[2] || ''

        if (content.trim() !== '' && containsJapanese(content)) {
          textNodes.push({
            node: child as Text,
            originalText: content,
            indent
          })
        }
      } else {
        recurse(child)
      }
    }
  }

  recurse(doc.body)

  return { doc, textNodes }
}

export function escapeHtml(str: string): string {
  return str
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll('\'', '&#039;')
}

export function containsJapanese(text: string): boolean {
  return /[\u3040-\u309F\u30A0-\u30FF\u31F0-\u31FF\u3400-\u4DBF\u4E00-\u9FFF\uFF66-\uFF9D]/.test(
    text
  )
}

export const handleTranslation = async ({
  modalId,
  q,
  onSuccess,
  onError
}: {
  modalId: string
  q: string[]
  onSuccess: (translations: any) => Promise<TRANSLATION_SAVE_PAYLOAD>
  onError: (message: string) => void
}) => {
  try {
    main.$blockui.show()
    const res = await UserService.translate({
      q,
      target: 'en',
      format: 'html'
    })
    const result = await onSuccess(res?.data?.data?.translations)
    await UserService.saveTranslation(result)
  } catch (err: any) {
    let errorMess = ''
    const errorMessAPI = err?.response?.data?.message
    if (errorMessAPI === ERR_API_KEY.TOO_MANY_SEGMENTS) {
      errorMess = i18n
        .t(`common.message.${errorMessAPI}`, {
          count: q.length
        })
        .toString()
    } else {
      errorMess = i18n.t('common.message.GOOGLE_TRANSLATE_API_ERROR').toString()
    }
    onError(errorMess)
    main.$nextTick(() => {
      main.$bvModal.show(modalId)
    })
  } finally {
    main.$blockui.hide()
  }
}

export const getScrollableContainer = (el: any) => {
  let current = el
  while (current && current !== document.body) {
    const overflowY = globalThis.getComputedStyle(current).overflowY
    if (overflowY === 'auto' || overflowY === 'scroll') {
      if (current.scrollHeight > current.clientHeight) {
        return current
      }
    }
    current = current.parentElement
  }
  return document.scrollingElement || document.documentElement
}

/**
 * convert html data format to text (remove html tags)
 */
export const removeHTML = (html: string) => {
  // Decode HTML entities
  const textarea = document.createElement('textarea')
  textarea.innerHTML = html
  const decoded = textarea.value

  // Remove all tags but keep whitespace/newlines
  return decoded.replace(/<[^>]*>/g, '')
}

/**
 * convert HTML to textarea type
 */
export const stripHtmlKeepFormat = (html: string) => {
  if (!html) return ''

  const textarea = document.createElement('textarea')
  textarea.innerHTML = html
  let decoded = textarea.value

  // Add newlines for block tags and <br>
  decoded = decoded
    .replace(/<(br|BR)\s*\/?>/g, '\n') // br = 1 newline
    .replace(/<\/(div|p|h[1-6]|li|tr)>/gi, '\n') // block close = 1 newline

  // Remove other tags
  decoded = decoded.replace(/<[^>]*>/g, '')

  // Collapse spaces
  decoded = decoded.replace(/[ \t]+/g, ' ')

  // Collapse 3+ newlines → 2 newlines (keep exactly 2 if they exist)
  decoded = decoded.replace(/\n\s*\n\s*\n+/g, '\n\n')

  return decoded.trim()
}

// Ensure we have a valid Range for inserting content
// If there is no saved caret position, create one at the end of the editor.
// Also removes the default <br> if the editor is empty.
export function ensureRange(
  editorEl: HTMLElement,
  savedRange: Range | null
): Range {
  let range = savedRange

  // No saved caret -> collapse to end of editor
  if (!range) {
    range = document.createRange()
    range.selectNodeContents(editorEl)
    range.collapse(false) // false = place caret at the end
  }

  // If editor only contains a single <br>, clear it
  if (
    editorEl.childNodes.length === 1 &&
    editorEl.firstChild?.nodeName === 'BR'
  ) {
    editorEl.innerHTML = ''
  }

  return range
}

function htmlToElement(html: any) {
  const template = document.createElement('template')
  html = html.trim() // Never return a text node of whitespace as the result
  template.innerHTML = html
  return template.content.firstChild as ChildNode
}

// Insert a mention HTML element at the current Range
// Moves caret right after the inserted mention
export function insertMention(range: Range, mentionHtml: string): Range {
  // Create the mention wrapper
  const newElement = document.createElement('span')
  newElement.dataset.atEmbedded = ''
  newElement.setAttribute('contenteditable', 'false')

  // Add spaces before and after mention (same as original vue-at)
  newElement.appendChild(document.createTextNode(' '))
  newElement.appendChild(htmlToElement(mentionHtml))
  newElement.appendChild(document.createTextNode(' '))

  // Insert mention into the range
  range.insertNode(newElement)

  // Add an empty text node separator after the mention
  // → prevents backspace from deleting multiple mentions at once
  const separator = document.createTextNode(' ')
  if (newElement.parentNode) {
    newElement.parentNode.insertBefore(separator, newElement.nextSibling)
  }

  // Place caret after the separator
  range.setStartAfter(separator)
  range.collapse(true)

  // Update Select selection
  applyRange(range)

  return range
}

export function isEditorEffectivelyEmpty(el: HTMLElement) {
  const clone = el.cloneNode(true) as HTMLElement

  clone.innerHTML = clone.innerHTML.replaceAll('&nbsp;', '').replace(/\s+/g, '')

  clone.querySelectorAll('*').forEach(child => {
    if (!child.textContent?.trim()) child.remove()
  })

  return clone.innerText.trim() === '' && clone.innerHTML.trim() === ''
}

export function applyRange(range: any) {
  const selection = globalThis.getSelection()
  if (selection) {
    // 容错
    selection.removeAllRanges()
    selection.addRange(range)
  }
}

export function getRange() {
  const selection = globalThis.getSelection()
  if (selection && selection.rangeCount > 0) {
    return selection.getRangeAt(0)
  }
  return null
}

export function getAtAndIndex(text: string, ats: any) {
  return ats
    .map((at: any) => {
      return { at, index: text.lastIndexOf(at) }
    })
    .reduce((a: any, b: any) => {
      return a.index > b.index ? a : b
    })
}

export function getPrecedingRange() {
  const r = getRange()
  if (r) {
    const range = r.cloneRange()
    range.collapse(true)
    // var el = closest(range.endContainer, d => d.contentEditable)
    // range.setStart(el, 0)
    range.setStart(range.endContainer, 0)
    return range
  }
}

export const handleMemberLoginStatus = ({
  tobeLoggedOut,
  lastLoginAt,
  isCurrentUser = false,
}: {
  tobeLoggedOut: boolean // true: user logged out, false: user online
  lastLoginAt: string
  isCurrentUser: boolean // true: viewing own status, false: viewing others' status
  }) => {
  // If the current user is viewing their own status, always show "online"
  if (isCurrentUser) {
    return i18n.t('modal_show_profile.online')
  }

  // If there's no login record, the user has never logged in
  if (!lastLoginAt) {
    return i18n.t('modal_show_profile.not_login')
  }

  // convert lastLoginAt and current time to same timezone (Asia/Tokyo)
  const current = momentTimeZone.tz('Asia/Tokyo')
  const lastLogin = momentTimeZone.tz(
    lastLoginAt,
    'YYYY-MM-DD HH:mm:ss',
    'Asia/Tokyo'
  )

  // calculate time difference
  const diffMinutes = current.diff(lastLogin, 'minutes')
  const diffHours = current.diff(lastLogin, 'hours')
  const diffDays = current.diff(lastLogin, 'days')

  // 3. Online status: Not logged out or t <= 0
  if (!tobeLoggedOut || diffMinutes <= 0) {
    return i18n.t('modal_show_profile.online')
  }

  // 15 mins ago: 0 < t <= 15
  if (diffMinutes <= 15) {
    return i18n.tc('modal_show_profile.minutes_ago', 15, {
      minutes: 15
    })
  }

  // 30 mins ago: 15 < t <= 30
  if (diffMinutes <= 30) {
    return i18n.tc('modal_show_profile.minutes_ago', 30, {
      minutes: 30
    })
  }

  // Within 1 hour: 30 < t <= 60
  if (diffMinutes <= 60) {
    return i18n.t('modal_show_profile.within_one_hours')
  }

  // 1 hour ago: 60 < t < 2 hours
  // Note: diffHours remains 1 until exactly 120 minutes (2 hours)
  if (diffHours < 2) {
    return i18n.tc('modal_show_profile.hours_ago', 1, {
      hours: 1
    })
  }

  // X hours ago: 2 hours <= t < 24 hours
  if (diffHours < 24) {
    return i18n.tc('modal_show_profile.hours_ago', diffHours, {
      hours: diffHours
    })
  }

  // X days ago: 24 hours <= t < 8 days
  // Table displays relative days up to "7 days ago" (t < 8 days)
  if (diffDays < 8) {
    return i18n.tc('modal_show_profile.days_ago', diffDays, {
      day: diffDays
    })
  }

  // Fixed Date: t >= 8 days
  // Display absolute format: yyyy/mm/dd hh:mm
  return lastLogin.format(
    i18n.t('common.date_format.yyyy_mm_dd_hh_mm').toString()
  )
}

export function getEmojiByNativeFunc(nativeChar: string, emojiIndex: any) {
  if (!nativeChar || !emojiIndex) return null;
  const found = emojiIndex._nativeEmojis[nativeChar];
  return found || null;
}

// Replace <video> and <iframe> and their parent elements with a placeholder text
export function replaceVideoAndParent(html: string) {
  const parser = new DOMParser()
  const doc = parser.parseFromString(html, 'text/html')

  const mediaElements = doc.querySelectorAll('video, iframe')

  mediaElements.forEach(el => {
    const span = doc.createElement('span');
    span.textContent = i18n.t('common.message.include_video').toString()
    span.style.color = '#999';
    el?.parentNode?.replaceChild(span, el);
  })

  return doc.body.innerHTML
}

export const hanldeUserStatus = (name: string, status: EUserStatus) => {
  if (status === EUserStatus.LEAVE_GROUP_TAG)
    return name + i18n.t('common.suffix.leave_group')
  if (status === EUserStatus.LEAVE_SYSTEM)
    return i18n.t('common.suffix.leave_system') as string
  return name
}